import type {
  AppModule,
  SettingsSection,
  ShellContext,
  SidebarEntry,
  SidebarId,
  SidebarSection,
} from "../modules/types";

/*
 * The app sidebar as the live Free-plan owner sees it (docs/spec/routes.md section 2). The shell owns
 * placement (section + order) so the sidebar is complete before feature modules exist; a module's
 * entry with the same id replaces the default entry's label, path, icon, visibility or flyout.
 */
export const sidebarPlacement: Record<SidebarId, SidebarSection> = {
  TIMESHEET: "MAIN",
  CALENDAR: "MAIN",
  TIME_TRACKER: "MAIN",
  DASHBOARD: "ANALYZE",
  REPORTS: "MANAGE",
  PROJECTS: "MANAGE",
  TEAM: "MANAGE",
  CLIENTS: "MANAGE",
  TAGS: "MANAGE",
  KIOSK: "SHOW_MORE",
  SCHEDULE: "SHOW_MORE",
  EXPENSES: "SHOW_MORE",
  TIME_OFF: "SHOW_MORE",
  ACTIVITY: "SHOW_MORE",
  APPROVALS: "SHOW_MORE",
  INVOICES: "SHOW_MORE",
  ACTIVITY_MONITORING: "SHOW_MORE",
};

export const defaultSidebar: SidebarEntry[] = [
  { id: "TIMESHEET", label: "Timesheet", path: "/timesheet", icon: "timesheet", alt: "View Timesheet" },
  { id: "CALENDAR", label: "Calendar", path: "/calendar", icon: "calendar", alt: "View Calendar" },
  { id: "TIME_TRACKER", label: "Time tracker", path: "/tracker", icon: "tracker", alt: "View Time tracker" },
  { id: "DASHBOARD", label: "Dashboard", path: "/dashboard", icon: "dashboard", alt: "View Dashboard" },
  {
    id: "REPORTS",
    label: "Reports",
    path: "/reports/summary",
    icon: "reports",
    alt: "View Reports",
    activePrefixes: ["/reports"],
    flyout: [
      {
        header: "Time",
        items: [
          { label: "Summary", path: "/reports/summary" },
          { label: "Detailed", path: "/reports/detailed" },
          { label: "Weekly", path: "/reports/weekly" },
          { label: "Shared", path: "/reports/shared" },
        ],
      },
      {
        header: "Team",
        items: [
          { label: "Attendance", path: "/reports/team/attendance" },
          { label: "Assignments", path: "/reports/team/assignments" },
        ],
      },
      { header: "Expense", items: [{ label: "Detailed", path: "/reports/expense/detailed" }] },
    ],
  },
  { id: "PROJECTS", label: "Projects", path: "/projects", icon: "projects", alt: "View Projects" },
  { id: "TEAM", label: "Team", path: "/teams", icon: "team", alt: "View team" },
  { id: "CLIENTS", label: "Clients", path: "/clients", icon: "clients", alt: "View Clients" },
  { id: "TAGS", label: "Tags", path: "/tags", icon: "tag", alt: "View tags" },
  { id: "KIOSK", label: "Kiosks", path: "/kiosks", icon: "kiosk", alt: "View Kiosks" },
  { id: "SCHEDULE", label: "Schedule", path: "/scheduling#projects", icon: "schedule", alt: "View Schedule" },
  { id: "EXPENSES", label: "Expenses", path: "/expenses", icon: "expenses", alt: "View Expenses" },
  { id: "TIME_OFF", label: "Time off", path: "/time-off#requests", icon: "time-off", alt: "View Time off" },
  {
    id: "ACTIVITY",
    label: "Activity",
    path: "/activity/screenshots",
    icon: "activity",
    alt: "View Activity",
    // `/activity-monitoring` is its own item; a bare `/activity` prefix would light both.
    activePrefixes: ["/activity/"],
  },
  { id: "APPROVALS", label: "Approvals", path: "/approvals#pending", icon: "approvals", alt: "View Approvals" },
  { id: "INVOICES", label: "Invoices", path: "/invoices", icon: "invoices", alt: "View Invoices" },
  {
    id: "ACTIVITY_MONITORING",
    label: "Monitoring",
    path: "/activity-monitoring",
    icon: "monitoring",
    alt: "Activity monitoring",
  },
];

export interface PlacedEntry extends SidebarEntry {
  section: SidebarSection;
}

export function mergeSidebar(modules: AppModule[]): PlacedEntry[] {
  const byId = new Map<SidebarId, SidebarEntry>(defaultSidebar.map((e) => [e.id, e]));
  for (const m of modules) for (const e of m.sidebar ?? []) byId.set(e.id, { ...byId.get(e.id), ...e });
  // Map insertion order follows defaultSidebar, which is the live order.
  return [...byId.values()].map((e) => ({ ...e, section: sidebarPlacement[e.id] }));
}

export function visibleSidebar(entries: PlacedEntry[], ctx: ShellContext): PlacedEntry[] {
  // CLONE rule (shell spec 5.1): owners and admins see every item; switched-off features open their off state.
  return entries.filter((e) => ctx.isAdmin || !e.visible || e.visible(ctx));
}

export function entryPathname(path: string): string {
  return path.split(/[?#]/)[0] ?? path;
}

export function isEntryActive(entry: SidebarEntry, pathname: string): boolean {
  const prefixes = entry.activePrefixes ?? [entryPathname(entry.path)];
  return prefixes.some((p) => pathname === p || pathname.startsWith(p.endsWith("/") ? p : `${p}/`));
}

/** Workspace-settings left nav (routes.md section 3); groups are separated by extra spacing. */
export const builtInSettingsSections: Array<Omit<SettingsSection, "Component">> = [
  { hash: "general", label: "General", group: 1, order: 10 },
  { hash: "time-tracking", label: "Time tracking", group: 1, order: 20 },
  { hash: "finances", label: "Finances", group: 1, order: 30 },
  { hash: "time-planning", label: "Time planning", group: 1, order: 40 },
  { hash: "user-activity", label: "User activity", group: 1, order: 50 },
  { hash: "kiosk", label: "Kiosk", group: 1, order: 60 },
  { hash: "security", label: "Security", group: 1, order: 70 },
  { hash: "accounts", label: "Accounts", group: 2, order: 10 },
  { hash: "permissions", label: "Permissions", group: 2, order: 20 },
  { hash: "integrations-addons", label: "Integrations & add-ons", group: 3, order: 10 },
  { hash: "import", label: "Import/Export", group: 3, order: 20 },
];

export function mergeSettingsSections(
  modules: AppModule[],
  Placeholder: SettingsSection["Component"],
): SettingsSection[] {
  const byHash = new Map<string, SettingsSection>(
    builtInSettingsSections.map((sec) => [sec.hash, { ...sec, Component: Placeholder }]),
  );
  for (const m of modules) for (const sec of m.settingsSections ?? []) byHash.set(sec.hash, sec);
  return [...byHash.values()].sort((a, b) => a.group - b.group || a.order - b.order);
}
